import React, { useEffect, useRef } from "react";
import { useIsMobile } from "@/hooks/useIsMobile";

// Custom coffee-cup cursor with subtle rising smoke trail. Desktop only.
export const CustomCursor = () => {
  const isMobile = useIsMobile();
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const smokeLayer = useRef(null);
  const pos = useRef({ x: -100, y: -100 });
  const ring = useRef({ x: -100, y: -100 });
  const lastSmoke = useRef(0);

  useEffect(() => {
    if (isMobile) return;
    document.body.classList.add("cursor-enabled");

    const onMove = (e) => {
      pos.current = { x: e.clientX, y: e.clientY };
      if (dotRef.current) {
        dotRef.current.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
      }
      const now = performance.now();
      if (now - lastSmoke.current > 90 && smokeLayer.current) {
        lastSmoke.current = now;
        spawnSmoke(e.clientX, e.clientY);
      }
    };

    const spawnSmoke = (x, y) => {
      const el = document.createElement("span");
      el.className = "mara-smoke";
      el.style.cssText = `position:fixed;left:${x}px;top:${y - 10}px;width:8px;height:8px;border-radius:50%;background:radial-gradient(circle,rgba(200,169,126,0.55),rgba(200,169,126,0));pointer-events:none;z-index:9998;transform:translate(-50%,-50%);`;
      smokeLayer.current.appendChild(el);
      const drift = (Math.random() - 0.5) * 22;
      el.animate(
        [
          { transform: "translate(-50%,-50%) scale(0.6)", opacity: 0.7 },
          { transform: `translate(calc(-50% + ${drift}px),-140%) scale(2.2)`, opacity: 0 },
        ],
        { duration: 1100 + Math.random() * 500, easing: "ease-out" }
      ).onfinish = () => el.remove();
    };

    const raf = () => {
      ring.current.x += (pos.current.x - ring.current.x) * 0.16;
      ring.current.y += (pos.current.y - ring.current.y) * 0.16;
      if (ringRef.current) {
        ringRef.current.style.transform = `translate(${ring.current.x}px, ${ring.current.y}px)`;
      }
      frame = requestAnimationFrame(raf);
    };
    let frame = requestAnimationFrame(raf);

    const onOver = (e) => {
      const interactive = e.target.closest("a, button, [role='button'], input, textarea, .cursor-link");
      if (ringRef.current) ringRef.current.dataset.active = interactive ? "true" : "false";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseover", onOver);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseover", onOver);
      document.body.classList.remove("cursor-enabled");
    };
  }, [isMobile]);

  if (isMobile) return null;
  return (
    <>
      <div ref={smokeLayer} aria-hidden="true" />
      <div
        ref={ringRef}
        data-active="false"
        aria-hidden="true"
        className="fixed left-0 top-0 z-[9999] pointer-events-none -translate-x-1/2 -translate-y-1/2 data-[active=true]:scale-150 transition-transform duration-200 ease-out"
        style={{ willChange: "transform" }}
      >
        <div className="w-9 h-9 rounded-full border border-gold/70 -ml-[18px] -mt-[18px]" />
      </div>
      <div
        ref={dotRef}
        aria-hidden="true"
        className="fixed left-0 top-0 z-[9999] pointer-events-none"
        style={{ willChange: "transform" }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" className="-ml-[11px] -mt-[11px]" fill="none" stroke="#C8A97E" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 8h1a4 4 0 1 1 0 8h-1" />
          <path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z" fill="#2B1B12" />
        </svg>
      </div>
    </>
  );
};

export default CustomCursor;
